import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { StaffAuthProvider, useStaffAuth } from './contexts/StaffAuthContext'
import { LoginPage } from './components/auth/LoginPage'
import { AccountSelectorPage } from './components/auth/AccountSelectorPage'
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './components/auth/ResetPasswordPage'
import { StaffLoginPage } from './components/auth/StaffLoginPage'
import { MainLayout } from './components/layout/MainLayout'
import { Dashboard } from './components/layout/Dashboard'
import { ControlCenterLayout } from './components/admin/ControlCenterLayout'
import { ControlCenterDashboard } from './components/admin/ControlCenterDashboard'
import { ControlCenterPlaceholder } from './components/admin/ControlCenterPlaceholder'
import { AccountsList } from './components/admin/AccountsList'
import { AccountDetail } from './components/admin/AccountDetail'
import { AccountOverviewTab } from './components/admin/AccountOverviewTab'
import { AccountUsersTab } from './components/admin/AccountUsersTab'
import { AccountNotesTab } from './components/admin/AccountNotesTab'
import { AccountPlaceholderTab } from './components/admin/AccountPlaceholderTab'
import { CreateAccountWizard } from './components/admin/CreateAccountWizard'
import { AnnouncementsList } from './components/admin/AnnouncementsList'
import { AnnouncementForm } from './components/admin/AnnouncementForm'
import { AnnouncementView } from './components/admin/AnnouncementView'
import { AccountFeaturesTab } from './components/admin/AccountFeaturesTab'
import { AccountWebsiteTab } from './components/admin/AccountWebsiteTab'
import { SystemHealthDashboard } from './components/admin/SystemHealthDashboard'
import { ReportsDashboard } from './components/admin/ReportsDashboard'
import { RoleManagement } from './components/settings/RoleManagement'
import { SettingsLayout } from './components/settings/SettingsLayout'
import { SettingsIndex } from './components/settings/SettingsIndex'
import { DealerProfileForm } from './components/settings/DealerProfileForm'
import { DescriptionTemplates } from './components/settings/DescriptionTemplates'
import { WarrantyTemplates } from './components/settings/WarrantyTemplates'
import { VideoSettings } from './components/settings/VideoSettings'
import { BannerSettings } from './components/settings/BannerSettings'
import { WindowStickerSettings } from './components/settings/WindowStickerSettings'
import { CreditAppSettings } from './components/settings/CreditAppSettings'
import { SalesChannelSettings } from './components/settings/SalesChannelSettings'
import { QuickDeskSettings } from './components/settings/QuickDeskSettings'
import { AdfSettings } from './components/settings/AdfSettings'
import { AppointmentSettings } from './components/settings/AppointmentSettings'
import { LocalListingSettings } from './components/settings/LocalListingSettings'
import { DealerDocSettings } from './components/settings/DealerDocSettings'
import { BillingOverview } from './components/settings/BillingOverview'

// Loading spinner component
function LoadingScreen(): React.ReactElement {
  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/70">Loading...</p>
      </div>
    </div>
  )
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { isLoading, isAuthenticated, needsAccountSelection, currentAccount } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Multi-account users need to select an account first
  if (needsAccountSelection && !currentAccount) {
    return <Navigate to="/select-account" replace />
  }

  return <>{children}</>
}

// Guest route wrapper (redirect authenticated users)
function GuestRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { isLoading, isAuthenticated, needsAccountSelection, currentAccount } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (isAuthenticated) {
    if (needsAccountSelection && !currentAccount) {
      return <Navigate to="/select-account" replace />
    }
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Account selection route wrapper
function AccountSelectionRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { isLoading, isAuthenticated, accounts, currentAccount } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Single account users don't need this page
  if (accounts.length <= 1) {
    return <Navigate to="/" replace />
  }

  // Already selected an account
  if (currentAccount) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Staff protected route wrapper
function StaffProtectedRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { isLoading, isAuthenticated } = useStaffAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />
  }

  return <>{children}</>
}

// Placeholder icons for sections not yet built
const placeholderIcons = {
  accounts: (
    <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  ),
  announcements: (
    <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  ),
  health: (
    <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  ),
  reports: (
    <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  settings: (
    <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
}

// Admin routes — login stays here, everything else redirects to Control Center
function AdminRoutes(): React.ReactElement {
  return (
    <StaffAuthProvider>
      <Routes>
        <Route path="login" element={<StaffLoginPage />} />
        <Route path="*" element={<Navigate to="/control_center" replace />} />
      </Routes>
    </StaffAuthProvider>
  )
}

// Control Center routes with sidebar layout
function ControlCenterRoutes(): React.ReactElement {
  return (
    <StaffAuthProvider>
      <Routes>
        <Route
          element={
            <StaffProtectedRoute>
              <ControlCenterLayout />
            </StaffProtectedRoute>
          }
        >
          <Route index element={<ControlCenterDashboard />} />
          <Route path="accounts" element={<AccountsList />} />
          <Route path="accounts/new" element={<CreateAccountWizard />} />
          <Route path="accounts/:id" element={<AccountDetail />}>
            <Route index element={<AccountOverviewTab />} />
            <Route path="users" element={<AccountUsersTab />} />
            <Route path="features" element={<AccountFeaturesTab />} />
            <Route path="website" element={<AccountWebsiteTab />} />
            <Route path="imports" element={<AccountPlaceholderTab title="Imports" description="Manage data import subscriptions and feed configurations for this account." ticketRef="VMS-8" />} />
            <Route path="exports" element={<AccountPlaceholderTab title="Exports" description="Manage data export subscriptions and feed destinations for this account." ticketRef="VMS-9" />} />
            <Route path="activity" element={<AccountPlaceholderTab title="Activity" description="Audit log for this account. Requires PaperTrail integration." ticketRef="VMS-14" />} />
            <Route path="notes" element={<AccountNotesTab />} />
          </Route>
          <Route path="announcements" element={<AnnouncementsList />} />
          <Route path="announcements/new" element={<AnnouncementForm />} />
          <Route path="announcements/:id" element={<AnnouncementView />} />
          <Route path="announcements/:id/edit" element={<AnnouncementForm />} />
          <Route path="system_health" element={<SystemHealthDashboard />} />
          <Route path="reports" element={<ReportsDashboard />} />
          <Route
            path="craigslist"
            element={
              <ControlCenterPlaceholder
                title="Craigslist Management"
                description="Manage Craigslist posting templates, schedules, and account settings."
                icon={placeholderIcons.reports}
                ticketRef="VMS-122"
              />
            }
          />
          <Route
            path="imports"
            element={
              <ControlCenterPlaceholder
                title="Imports Management"
                description="Configure and monitor data import feeds and schedules."
                icon={placeholderIcons.settings}
                ticketRef="VMS-123"
              />
            }
          />
          <Route
            path="exports"
            element={
              <ControlCenterPlaceholder
                title="Exports Management"
                description="Configure and monitor data export feeds and destinations."
                icon={placeholderIcons.settings}
                ticketRef="VMS-124"
              />
            }
          />
          <Route
            path="settings"
            element={
              <ControlCenterPlaceholder
                title="Settings"
                description="Control Center configuration and preferences."
                icon={placeholderIcons.settings}
              />
            }
          />
        </Route>
      </Routes>
    </StaffAuthProvider>
  )
}

export function AppRouter(): React.ReactElement {
  return (
    <BrowserRouter>
      <Routes>
        {/* Guest Routes */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestRoute>
              <ForgotPasswordPage />
            </GuestRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <GuestRoute>
              <ResetPasswordPage />
            </GuestRoute>
          }
        />

        {/* Admin Routes (legacy dashboard) */}
        <Route path="/admin/*" element={<AdminRoutes />} />

        {/* Control Center Routes (new sidebar layout) */}
        <Route path="/control_center/*" element={<ControlCenterRoutes />} />

        {/* Account Selection (authenticated but no account selected) */}
        <Route
          path="/select-account"
          element={
            <AccountSelectionRoute>
              <AccountSelectorPage />
            </AccountSelectionRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings Routes */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SettingsLayout />
              </MainLayout>
            </ProtectedRoute>
          }
        >
          <Route index element={<SettingsIndex />} />
          <Route path="profile" element={<DealerProfileForm />} />
          <Route path="descriptions" element={<DescriptionTemplates />} />
          <Route path="warranties" element={<WarrantyTemplates />} />
          <Route path="video" element={<VideoSettings />} />
          <Route path="banner" element={<BannerSettings />} />
          <Route path="window-stickers" element={<WindowStickerSettings />} />
          <Route path="credit-app" element={<CreditAppSettings />} />
          <Route path="sales-channels" element={<SalesChannelSettings />} />
          <Route path="quick-desk" element={<QuickDeskSettings />} />
          <Route path="ilm" element={<AdfSettings />} />
          <Route path="appointments" element={<AppointmentSettings />} />
          <Route path="local-listing" element={<LocalListingSettings />} />
          <Route path="dealerdoc" element={<DealerDocSettings />} />
          <Route path="billing" element={<BillingOverview />} />
          <Route path="roles" element={<RoleManagement />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
